/**
 * The one place that picks a deal's systems diagram by product, so the deal page and the SOW
 * export can never draw different pictures:
 * - Maxio: left-to-right logo diagram derived from the Maxio answers.
 * - Every other product (Rillet, NetSuite, Salesforce, ...): the same logo diagram from the deal's
 *   tech-stack systems, with the deal's own product - name and logo - in the centre.
 */
import { landscapeInitials } from './logos.js';
import { buildMaxioFlowLandscape } from './maxio-flow-layout.js';
import { buildMaxioLandscape, isMaxioProductKey } from './maxio-landscape.js';
import { buildRilletLandscape, isRilletProductKey, RILLET_FLOW_OPTIONS, rilletLogoKeyForName, } from './rillet-landscape.js';
import { DEFAULT_LANDSCAPE_HUB_NAME } from './tech-stack.js';
/** Centre card per product (name + bundled logo key), keyed by the bare product key. */
const HUB_BY_PRODUCT = {
    netsuite: { name: 'NetSuite', logoKey: 'netsuite' },
    rillet: { name: 'Rillet', logoKey: 'rillet' },
    maxio: { name: 'Maxio', logoKey: 'maxio' },
    salesforce: { name: 'Salesforce', logoKey: 'salesforce' },
};
function hubForProduct(productKey) {
    const bare = productKey?.toLowerCase().split(':').pop()?.trim();
    return (bare && HUB_BY_PRODUCT[bare]) || null;
}
/**
 * The name the centre of a deal's diagram (and the tech-stack editor's "integrates with" list)
 * shows. Takes a bare or composite key ('netsuite' / 'oracle:netsuite'; the DB stores the
 * composite one). Unknown or missing product -> the neutral default, never another product's name.
 */
export function landscapeHubNameForProduct(productKey) {
    return hubForProduct(productKey)?.name ?? DEFAULT_LANDSCAPE_HUB_NAME;
}
/**
 * The Rillet logo diagram (same columns, plan colours and name-to-logo matching) with any product
 * in the centre. The centre card shows that product's logo, or an initials badge when none is
 * bundled.
 */
export function buildProductFlowLandscape(systems, hub) {
    const withLogos = systems.map((s) => {
        const logoKey = s.logoKey ?? rilletLogoKeyForName(s.name);
        return logoKey ? { ...s, logoKey } : s;
    });
    return buildMaxioFlowLandscape(withLogos, {
        ...RILLET_FLOW_OPTIONS,
        hubName: hub.name,
        hubLogoKey: hub.logoKey ?? '',
        hubInitials: landscapeInitials(hub.name),
    });
}
export function buildDealSystemLandscape(input) {
    if (isMaxioProductKey(input.productKey))
        return buildMaxioLandscape(input.answers);
    if (isRilletProductKey(input.productKey) && !input.hubName) {
        return buildRilletLandscape(input.systems());
    }
    const hub = hubForProduct(input.productKey);
    return buildProductFlowLandscape(input.systems(), {
        name: input.hubName ?? hub?.name ?? DEFAULT_LANDSCAPE_HUB_NAME,
        logoKey: hub?.logoKey,
    });
}
//# sourceMappingURL=deal-landscape.js.map